import React, { useEffect, useState } from 'react';
import { API_BASE, authHeaders } from '../../utils/api';
import '../../styles/admin-common.css';
import './NoticesCirculars.css';

const NoticesCirculars = () => {
  const [examNotices, setExamNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    news_title: '',
    news_date: '',
    notice_type: 'notice',
    file: null,
    link_text: '',
    link_url: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || '');
      } catch (e) {
        console.error('Error parsing token', e);
      }
    }
    fetchExamNotices();
  }, [statusFilter]);

  const fetchExamNotices = async () => {
    setLoading(true);
    setError('');
    try {
      let url = `${API_BASE}/api/admin/exam-notices?notice_type=notice`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      const res = await fetch(url, { headers: authHeaders() });
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}. ${text.substring(0, 80)}`);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load notices');
      setExamNotices(data.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load notices');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5 MB');
        return;
      }
      setForm((prev) => ({ ...prev, file }));
    }
  };

  const resetForm = () => {
    setForm({
      news_title: '',
      news_date: '',
      notice_type: 'notice',
      file: null,
      link_text: '',
      link_url: '',
    });
    setEditingId(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.news_title) {
      setError('Title is required');
      return;
    }

    const formData = new FormData();
    formData.append('news_title', form.news_title);
    if (form.news_date) {
      formData.append('news_date', form.news_date);
    }
    formData.append('category', 'notice');
    if (form.file) {
      formData.append('file', form.file);
    }
    formData.append('link_text', form.link_text);
    formData.append('link_url', form.link_url);

    try {
      const url = editingId
        ? `${API_BASE}/api/admin/exam-notices/${editingId}`
        : `${API_BASE}/api/admin/exam-notices`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: formData,
      });

      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save notice');

      setSuccess(data.message || 'Saved');
      const wasEditing = editingId;
      resetForm();
      await fetchExamNotices();
      if (wasEditing) window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message || 'Failed to save notice');
    }
  };

  const handleEdit = (item) => {
    setForm({
      news_title: item.news_title,
      news_date: item.news_date,
      notice_type: 'notice',
      file: null,
      link_text: item.link_text || '',
      link_url: item.link_url || '',
    });
    setEditingId(item.id);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/exam-notices/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to delete');
      setSuccess('Deleted');
      await fetchExamNotices();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleApprove = async (id) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/exam-notices/${id}/approve`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to approve');
      setSuccess('Approved');
      await fetchExamNotices();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this entry?')) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/exam-notices/${id}/reject`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to reject');
      setSuccess('Rejected');
      await fetchExamNotices();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-page notices-circulars-page">
      <div className="page-heading">
        <h1>Notices & Circulars</h1>
        <p>Manage exam notices and circulars with approval workflow.</p>
      </div>

      {error && <div className="admin-alert error">{error}</div>}
      {success && <div className="admin-alert success">{success}</div>}



      <div className="admin-grid notices-circulars-grid">
        <div className="card">
          <h2>{editingId ? 'Edit Notice/Circular' : 'Add Notice/Circular'}</h2>
          <form className="admin-form notice-circular-form" onSubmit={handleSubmit}>
            <label>
              Title *
              <input name="news_title" value={form.news_title} onChange={handleChange} required />
            </label>
            <label>
              Date
              <input
                type="text"
                name="news_date"
                value={form.news_date}
                onChange={handleChange}
                placeholder="e.g., 03 May 2024"
              />
            </label>
            <label>
              File (PDF or Image, Max 5 MB)
              <input type="file" accept=".pdf,image/*" onChange={handleFileChange} />
            </label>
            <label>
              Link Text (Optional)
              <input name="link_text" value={form.link_text} onChange={handleChange} placeholder="e.g., Download PDF" />
            </label>
            <label>
              Link URL (Optional)
              <input name="link_url" value={form.link_url} onChange={handleChange} placeholder="e.g., https://example.com/document.pdf" />
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{editingId ? 'Update' : 'Add'}</button>
              {editingId && (
                <button type="button" className="btn-secondary" onClick={resetForm}>Cancel</button>
              )}
            </div>
          </form>
        </div>

        <div className="card exam-list-card">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
            <h2>Notice & Circular Entries</h2>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px'}}>
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          {loading ? (
            <p>Loading...</p>
          ) : examNotices.length === 0 ? (
            <p>No entries yet.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Date</th>
                  <th>File/Link</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {examNotices.map((item) => (
                  <tr key={item.id}>
                    <td>{item.news_title}</td>
                    <td>{item.news_date}</td>
                    <td>
                      {item.file_path && (
                        <a href={`${API_BASE}${item.file_path}`} target="_blank" rel="noreferrer">
                          {item.link_text || 'Click here to open file'}
                        </a>
                      )}
                      {!item.file_path && item.link_url && (
                        <a href={item.link_url} target="_blank" rel="noreferrer">
                          {item.link_text || 'Click here to open link'}
                        </a>
                      )}
                      {!item.file_path && !item.link_url && 'No file/link'}
                    </td>
                    <td><span className={`status status-${item.status}`}>{item.status}</span></td>
                    <td className="actions">
                      <button className="btn-edit" onClick={() => handleEdit(item)}>✏️ Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(item.id)}>🗑️ Delete</button>
                      {item.status === 'pending' && userRole === 'superadmin' && (
                        <>
                          <button className="btn-approve" onClick={() => handleApprove(item.id)}>✓ Approve</button>
                          <button className="btn-reject" onClick={() => handleReject(item.id)}>✗ Reject</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoticesCirculars;
